import React from 'react';
import { Button } from '../ui/button';
import { MessageSquare, Sparkles, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { CommentPreviewResponse } from '../../lib/types';
interface CommentsPreviewProps {
  preview: CommentPreviewResponse | null;
  loading: boolean;
  error: string | null;
  alreadyFetched: boolean;
  onFetchClick: () => void;
}
export function CommentsPreview({
  preview,
  loading,
  error,
  alreadyFetched,
  onFetchClick
}: CommentsPreviewProps) {
  if (loading) {
    return <div className="border border-border rounded-lg p-4" data-id="element-1189">
        <div className="animate-pulse space-y-3" data-id="element-1190">
          <div className="h-4 bg-secondary rounded w-3/4" data-id="element-1191"></div>
          <div className="h-4 bg-secondary rounded w-1/2" data-id="element-1192"></div>
          <div className="h-20 bg-secondary rounded" data-id="element-1193"></div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="border border-destructive/20 rounded-lg p-4 bg-destructive/5" data-id="element-1194">
        <div className="flex items-center gap-2 text-destructive mb-2" data-id="element-1195">
          <AlertCircle size={16} data-id="element-1196" />
          <span className="text-sm font-medium" data-id="element-1197">Failed to load preview</span>
        </div>
        <p className="text-xs text-muted-foreground" data-id="element-1198">{error}</p>
      </div>;
  }
  if (!preview) return null;
  return <div className="border border-border rounded-lg hover:border-primary/30 transition-colors" data-id="element-1199">
      <div className="p-4" data-id="element-1200">
        <div className="flex items-start justify-between mb-3" data-id="element-1201">
          <div data-id="element-1202">
            <div className="flex items-center gap-2 mb-1" data-id="element-1203">
              <MessageSquare size={16} className="text-primary" data-id="element-1204" />
              <h4 className="font-medium text-sm" data-id="element-1205">Comment Data Extraction</h4>
              {alreadyFetched && <span className="text-xs bg-green-500/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1206">
                  <CheckCircle size={10} data-id="element-1207" />
                  Previously fetched
                </span>}
            </div>
            <p className="text-xs text-muted-foreground" data-id="element-1208">
              Preview of first {preview.preview_comments.length} comments
            </p>
          </div>
          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full whitespace-nowrap" data-id="element-1209">
            {preview.credits_needed}{' '}
            {preview.credits_needed === 1 ? 'credit' : 'credits'}
          </span>
        </div>

        {/* Preview Comments */}
        <div className="space-y-2 mb-4" data-id="element-1210">
          {preview.preview_comments.slice(0, 3).map((comment, index) => <div key={index} className="bg-secondary/30 rounded-lg p-3 text-sm" data-id="element-1211">
              <div className="flex items-start justify-between mb-1" data-id="element-1212">
                <span className="font-medium text-xs" data-id="element-1213">
                  {comment.author_name || comment.author}
                </span>
                <span className="text-xs text-muted-foreground flex items-center gap-1" data-id="element-1214">
                  <TrendingUp size={10} data-id="element-1215" />
                  {comment.likes.toLocaleString()}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2" data-id="element-1216">
                {comment.text || comment.comment}
              </p>
            </div>)}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-secondary/20 rounded-lg" data-id="element-1217">
          <div data-id="element-1218">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1219">
              Estimated Total
            </div>
            <div className="text-lg font-semibold" data-id="element-1220">
              {preview.estimated_total.toLocaleString()}
            </div>
          </div>
          <div data-id="element-1221">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1222">
              Credits Needed
            </div>
            <div className="text-lg font-semibold text-primary" data-id="element-1223">
              {preview.credits_needed}
            </div>
          </div>
        </div>

        {/* Info Message */}
        <div className="mb-4 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-600 dark:text-blue-400" data-id="element-1224">
          <Sparkles size={12} className="inline mr-1" data-id="element-1225" />
          {preview.message}
        </div>

        {alreadyFetched && <div className="mb-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2" data-id="element-1226">
            <AlertCircle size={14} data-id="element-1227" />
            You've already fetched comments for this video. Fetching again will
            use {preview.credits_needed} more{' '}
            {preview.credits_needed === 1 ? 'credit' : 'credits'}.
          </div>}

        {/* CTA Button */}
        <Button onClick={onFetchClick} className="w-full" size="sm" data-id="element-1228">
          <MessageSquare size={14} className="mr-2" data-id="element-1229" />
          {alreadyFetched ? `Fetch Again (${preview.credits_needed} ${preview.credits_needed === 1 ? 'credit' : 'credits'})` : `Fetch All Comments (${preview.credits_needed} ${preview.credits_needed === 1 ? 'credit' : 'credits'})`}
        </Button>
      </div>
    </div>;
}